import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ApiError } from "@/lib/api";
import { useRequestMagicLink, useSignInWithGoogle } from "@/lib/session";
import { BusyButton, ButtonLink } from "@/components/button";
import { LoginCard, LoginCardSubtitle, LoginCardTitle, Notice } from "@/components/ui/login-card";
import { FieldError, Input, Label } from "@/components/ui/field";
import { GoogleMark } from "@/components/google-mark";

const schema = z.object({
  // The accepting predicate, not a list of rejected inputs — the API decides
  // for real, and this only spares a round trip on an obvious typo.
  email: z.string().min(1, "Enter your email address").email("That is not an email address"),
});

type Values = z.infer<typeof schema>;

export type LoginMode = "login" | "register";

/**
 * Sign in with a magic link.
 *
 * Everything about this screen that looks like a detail is a fix that already
 * cost a round of review, so the flip to React keeps each one and says which:
 *
 *  - **one request per submit**, whether the second arrives by a double click,
 *    by Enter while the first is in flight, or from an assistive device. The
 *    submit button is disabled for the whole mutation, which is what the
 *    hand-written `submit-guard.js` did — and it is the button, not a flag in
 *    a handler, because Enter in a form does not always run one
 *    (`sitebrew-console#7`);
 *  - **the phone keyboard**: `inputmode="email"` puts the @ where a thumb
 *    expects it, and nothing capitalises, corrects or spellchecks an address.
 *    `autocomplete="email"` and deliberately not `"username"` — iOS offers
 *    saved passwords here because it reads the shape of the whole form, one
 *    field and a submit button being its picture of a login, and `"username"`
 *    was tried against exactly that on a preview and changed nothing;
 *  - **"check your inbox" only when the API says it sent one**. A delivery
 *    failure behind that screen is a login that looks healthy while nobody can
 *    get in (`sitebrew-console#4`).
 */
export function LoginScreen({
  mode = "login",
  next,
  failedProvider,
}: {
  mode?: LoginMode;
  next?: string;
  failedProvider?: string;
}) {
  const isRegister = mode === "register";
  // Where the link lands. `next` is only ever a same-origin path by the time it
  // reaches here (`sameOriginPath`), and it is how somebody sent here mid-way
  // through an OAuth authorization gets back to it.
  const requestLink = useRequestMagicLink(next ?? "/");
  // The same address for the other route in, so which one somebody picks does
  // not decide where they end up.
  const google = useSignInWithGoogle(next ?? "/");

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  if (requestLink.isSuccess) {
    return (
      <LoginCard>
        <LoginCardTitle>Check your inbox</LoginCardTitle>
        <LoginCardSubtitle>
          If that address can sign in, a link is on its way. It works once and expires in 10
          minutes.
        </LoginCardSubtitle>
        {/*
          A link, as in the original (`<a class="btn btn-ghost" href="/login">`),
          and not only for the markup: a `<button>` keeps the browser's own
          line-height where an `<a>` inherits the page's 1.6, so drawn as a
          button this control stood 7px shorter and pulled the whole card up
          with it — the last difference between this screen and production.

          A plain anchor and not the router's `Link`: the destination is the
          route this screen is already on, so a client-side navigation would
          not remount anything and the screen would have to be talked out of
          itself by resetting the request. Reloading is what the original does,
          it is what empties the field, and it costs a cached document.
        */}
        <ButtonLink className="mt-[.9rem] w-full" variant="outline" href="/login">
          ← Back
        </ButtonLink>
      </LoginCard>
    );
  }

  // 429 is the one an ordinary person can act on; everything else is ours, and
  // says so rather than blaming the address they typed.
  const failure = requestLink.error;
  const tooMany = failure instanceof ApiError && failure.status === 429;

  // Google fails in two places and only one of them is still on this screen:
  // the API refusing to start the round is held by the mutation, while a round
  // that failed at Google or in the callback comes back as `?error=` on this
  // address, long after any state of ours is gone.
  const googleTrouble = googleFailure(google.error, failedProvider);

  return (
    <LoginCard>
      <LoginCardTitle>{isRegister ? "Create your account" : "Sign in"}</LoginCardTitle>
      <LoginCardSubtitle>
        {isRegister ? "Start building your site in minutes." : "Welcome back to Sitebrew."}
      </LoginCardSubtitle>

      {googleTrouble ? <Notice className="mb-5">{googleTrouble}</Notice> : null}

      {failure ? (
        <Notice className="mb-5">
          {tooMany
            ? "Too many attempts. Wait a few minutes and try again."
            : "The sign-in service could not send the link. Nothing was sent — please try again."}
        </Notice>
      ) : null}

      <form
        noValidate
        onSubmit={form.handleSubmit((values) => requestLink.mutate(values.email))}
      >
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@company.com"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          aria-invalid={form.formState.errors.email ? true : undefined}
          {...form.register("email")}
        />
        <FieldError>{form.formState.errors.email?.message}</FieldError>

        <BusyButton className="mt-[.9rem] w-full" type="submit" busy={requestLink.isPending} busyLabel="Sending…">
          {isRegister ? "Send sign-up link" : "Send magic link"}
        </BusyButton>
      </form>

      <div className="my-[1.3rem] flex items-center gap-[.8rem] text-xs text-muted-foreground before:h-px before:flex-1 before:bg-border before:content-[''] after:h-px after:flex-1 after:bg-border after:content-['']">
        or
      </div>

      {/*
        Google, connected for real in `sitebrew-api#43`. It is a button and no
        longer a link because what it starts is a POST — see
        `useSignInWithGoogle` for why an `href` cannot do it.

        Busy through `isSuccess` as well as `isPending`: the successful end of
        that mutation is a navigation away from this document, and a button that
        goes back to "Continue with Google" for the moment the browser takes to
        leave invites the second click that starts a second round.
      */}
      <BusyButton
        className="mt-[.9rem] w-full"
        type="button"
        variant="outline"
        busy={google.isPending || google.isSuccess}
        busyLabel="Taking you to Google…"
        onClick={() => google.mutate(undefined, { onSuccess: (url) => window.location.assign(url) })}
      >
        <GoogleMark /> Continue with Google
      </BusyButton>

      <div className="mt-[1.3rem] text-center text-sm text-muted-foreground">
        {isRegister ? (
          <>
            Already have an account? <a className="text-primary" href="/login">Sign in</a>
          </>
        ) : (
          <>
            New to Sitebrew?{" "}
            <a className="text-primary" href="/login?mode=register">
              Create an account
            </a>
          </>
        )}
      </div>
    </LoginCard>
  );
}

/**
 * What to say about a Google round that did not work.
 *
 * Every sentence ends with what is true of the account, because that is the
 * question somebody who was just bounced through two sites actually has. The
 * codes are the library's own (`better-auth`), and an unknown one falls to the
 * general sentence rather than being shown raw — a code on the screen is a
 * detail nobody outside this repository can act on, and this value arrives from
 * the query string.
 */
function googleFailure(failure: unknown, returned: string | undefined): string | undefined {
  if (failure) {
    // 404 is the stage saying it has no Google at all — a preview, or an
    // environment where the credentials were never set. Telling somebody to
    // try again there would be a loop with no exit.
    return failure instanceof ApiError && failure.status === 404
      ? "Google sign-in isn't available here. Use a magic link instead."
      : "Google sign-in could not be started. Nothing changed — please try again.";
  }

  switch (returned) {
    case undefined:
      return undefined;
    case "access_denied":
      return "Google sign-in was cancelled. Nothing changed.";
    case "account_not_linked":
      // The one refusal that is a decision and not a fault: the address already
      // has an account here and Google has not confirmed it belongs to this
      // Google account, so the two are not joined (`sitebrew-api#43`).
      return "That address already has a Sitebrew account, and Google hasn't confirmed the address belongs to this Google account. Sign in with a magic link instead.";
    default:
      return "Google couldn't sign you in. Nothing changed — please try again.";
  }
}
